// src/components/NotificationsCenterModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaFilter, FaDownload, FaChevronRight } from 'react-icons/fa';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import './NotificationsCenterModal.css';

const MAX_MODAL_NOTIFICATIONS = 60;
const SUMMARY_ITEMS = 6;

const normalizeCategory = (value) => {
    if (!value) {
        return 'general';
    }
    const base = String(value).toLowerCase().trim();
    try {
        return base
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    } catch (error) {
        return base;
    }
};

const normalizeActorRole = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const toDate = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value.toDate === 'function') {
        try {
            return value.toDate();
        } catch (error) {
            return null;
        }
    }
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === 'number') {
        return new Date(value);
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : new Date(parsed);
    }
    return null;
};

const formatDateTime = (value) => {
    const date = toDate(value);
    if (!date) {
        return 'Fecha no disponible';
    }
    return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatLabel = (value) => {
    if (!value) {
        return 'General';
    }
    return String(value)
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(' ');
};

const buildCsv = (rows) => {
    // Column headers without accents ensure Excel keeps UTF-8 content when opening CSV.
    const header = ['Fecha', 'Titulo', 'Descripcion', 'Categoria', 'Prioridad'];
    const body = rows.map((row) => (
        [
            formatDateTime(row.fecha),
            row.titulo || 'Notificación',
            row.descripcion ? row.descripcion.replace(/\r?\n/g, ' ') : '',
            formatLabel(row.categoria),
            formatLabel(row.prioridad || 'Normal'),
        ]
            .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(',')
    ));
    return [header.join(','), ...body].join('\n');
};

const NotificationsCenterModal = ({ fraccionamientoId, categorySet, allowedActorRoles, currentUserId, onClose }) => {
    const [tab, setTab] = useState('summary');
    const [notifications, setNotifications] = useState([]);
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);

    useEffect(() => {
        if (!fraccionamientoId) {
            return undefined;
        }
        const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones');
        const q = query(ref, orderBy('fecha', 'desc'), limit(MAX_MODAL_NOTIFICATIONS));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            let items = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
            
            // Filtrar notificaciones por destinatario si currentUserId está presente
            if (currentUserId) {
                items = items.filter((item) => {
                    // Mostrar si no tiene destinatario (notificación general) o si es para este usuario
                    return !item.destinatarioUid || item.destinatarioUid === currentUserId;
                });
            }
            
            setNotifications(items);
        }, (error) => {
            console.error('NotificationsCenterModal: error al escuchar notificaciones', error);
            setNotifications([]);
        });
        return () => unsubscribe();
    }, [fraccionamientoId, currentUserId]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (onClose) {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const allowedNotifications = useMemo(() => {
        return notifications.filter((item) => {
            if (categorySet && categorySet.size > 0) {
                if (!categorySet.has(normalizeCategory(item.categoria))) {
                    return false;
                }
            }
            if (allowedActorRoles && allowedActorRoles.size > 0) {
                if (!allowedActorRoles.has(normalizeActorRole(item.actorRol))) {
                    return false;
                }
            }
            return true;
        });
    }, [notifications, categorySet, allowedActorRoles]);

    const categoryMap = useMemo(() => {
        const map = new Map();
        allowedNotifications.forEach((item) => {
            const key = normalizeCategory(item.categoria);
            if (!map.has(key)) {
                map.set(key, item.categoria || 'General');
            }
        });
        return map;
    }, [allowedNotifications]);

    const filteredNotifications = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return allowedNotifications.filter((item) => {
            const categoryKey = normalizeCategory(item.categoria);
            if (selectedCategories.length > 0 && !selectedCategories.includes(categoryKey)) {
                return false;
            }
            if (needle) {
                const haystack = [
                    item.titulo,
                    item.descripcion,
                    item.categoria,
                    item.prioridad,
                    item.tipoMovimiento,
                    item.origen,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!haystack.includes(needle)) {
                    return false;
                }
            }
            return true;
        });
    }, [allowedNotifications, search, selectedCategories]);

    const summaryNotifications = useMemo(
        () => filteredNotifications.slice(0, SUMMARY_ITEMS),
        [filteredNotifications]
    );

    const categoryOptions = useMemo(
        () => Array.from(categoryMap.entries())
            .map(([key, label]) => ({ key, label: formatLabel(label) }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es')),
        [categoryMap]
    );

    const summaryStats = useMemo(() => {
        const counts = new Map();
        allowedNotifications.forEach((item) => {
            const key = normalizeCategory(item.categoria);
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        const topCategories = Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([key, total]) => ({
                key,
                label: formatLabel(categoryMap.get(key) || key),
                total,
            }));
        return {
            total: allowedNotifications.length,
            categories: topCategories,
        };
    }, [allowedNotifications, categoryMap]);

    const handleToggleCategory = (key) => {
        setSelectedCategories((prev) => (
            prev.includes(key)
                ? prev.filter((item) => item !== key)
                : [...prev, key]
        ));
    };

    const handleClearFilters = () => {
        setSelectedCategories([]);
        setSearch('');
    };

    const handleExport = () => {
        if (!filteredNotifications.length) {
            return;
        }

        const dataForExcel = filteredNotifications.map((row) => ({
            'Fecha y Hora': formatDateTime(row.fecha),
            'Título': row.titulo || 'Notificación',
            'Descripción': row.descripcion || row.mensaje || '',
            'Categoría': formatLabel(row.categoria),
            'Prioridad': formatLabel(row.prioridad || 'Normal'),
            'Registrado Por': row.actorNombre || row.creadoPor || 'Sistema'
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);

        // Auto-ajustar ancho de columnas
        const colWidths = [
            { wch: 22 }, // Fecha y Hora
            { wch: 30 }, // Título
            { wch: 55 }, // Descripción
            { wch: 18 }, // Categoría
            { wch: 14 }, // Prioridad
            { wch: 24 }, // Registrado Por
        ];
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Actividad Reciente');

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const filename = `Actividad_Reciente_${year}-${month}-${day}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    const renderSummaryView = () => (
        <div className="notifications-center-section" role="tabpanel" aria-labelledby="notificationsSummaryTab">
            <div className="notifications-summary-header">
                <div>
                    <h4>Resumen rápido</h4>
                    <p>{summaryStats.total} notificaciones registradas recientemente.</p>
                </div>
                <div className="notifications-summary-actions">
                    <button
                        type="button"
                        className="notifications-control"
                        onClick={() => setShowFilters((prev) => !prev)}
                    >
                        <FaFilter />
                        Filtro
                    </button>
                    <button
                        type="button"
                        className="notifications-control"
                        onClick={handleExport}
                        disabled={!filteredNotifications.length}
                    >
                        <FaDownload />
                        Excel
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="notifications-filters" role="region" aria-label="Filtros de notificaciones">
                    <header>
                        <strong>Filtrar por categoría</strong>
                        {(selectedCategories.length > 0 || search) && (
                            <button type="button" className="notifications-reset" onClick={handleClearFilters}>
                                Limpiar filtros
                            </button>
                        )}
                    </header>
                    <div className="notifications-filter-chips">
                        {categoryOptions.length === 0 && (
                            <span className="notifications-filter-empty">No hay categorías disponibles.</span>
                        )}
                        {categoryOptions.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                className={`notifications-filter-chip ${selectedCategories.includes(option.key) ? 'selected' : ''}`}
                                onClick={() => handleToggleCategory(option.key)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <label className="notifications-search" htmlFor="notificationsSummarySearch">
                        <span>Buscar</span>
                        <input
                            id="notificationsSummarySearch"
                            type="search"
                            placeholder="Buscar por título, descripción o tipo..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </label>
                </div>
            )}

            {summaryStats.categories.length > 0 && (
                <ul className="notifications-summary-stats" aria-label="Totales por categoría">
                    {summaryStats.categories.map((item) => (
                        <li key={item.key}>
                            <span>{item.label}</span>
                            <strong>{item.total}</strong>
                        </li>
                    ))}
                </ul>
            )}

            {summaryNotifications.length > 0 ? (
                <ul className="notifications-summary-list">
                    {summaryNotifications.map((item) => (
                        <li key={item.id} className="notifications-summary-item">
                            <div className="notifications-summary-title">{item.titulo || 'Notificación'}</div>
                            {item.descripcion && (
                                <p className="notifications-summary-description">{item.descripcion}</p>
                            )}
                            <div className="notifications-summary-meta">
                                <span className="notifications-tag">{formatLabel(item.categoria)}</span>
                                <time dateTime={toDate(item.fecha)?.toISOString() || undefined}>{formatDateTime(item.fecha)}</time>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="notifications-empty">No encontramos notificaciones con los filtros actuales.</p>
            )}

            <div className="notifications-summary-footer">
                <button
                    type="button"
                    className="notifications-link"
                    onClick={() => setTab('detail')}
                >
                    Ver todas las notificaciones
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );

    const renderDetailView = () => (
        <div className="notifications-center-section" role="tabpanel" aria-labelledby="notificationsDetailTab">
            <div className="notifications-detail-controls">
                <label htmlFor="notificationsDetailSearch" className="notifications-search-inline">
                    <span className="sr-only">Buscar</span>
                    <input
                        id="notificationsDetailSearch"
                        type="search"
                        placeholder="Buscar por título, descripción o tipo..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </label>
                <button
                    type="button"
                    className="notifications-control"
                    onClick={() => setShowFilters((prev) => !prev)}
                >
                    <FaFilter />
                    Filtros avanzados
                </button>
                <button
                    type="button"
                    className="notifications-control"
                    onClick={handleExport}
                    disabled={!filteredNotifications.length}
                >
                    <FaDownload />
                    Exportar a Excel
                </button>
            </div>

            {showFilters && (
                <div className="notifications-filters" role="region" aria-label="Filtros de notificaciones">
                    <header>
                        <strong>Filtrar por categoría</strong>
                        {(selectedCategories.length > 0 || search) && (
                            <button type="button" className="notifications-reset" onClick={handleClearFilters}>
                                Limpiar filtros
                            </button>
                        )}
                    </header>
                    <div className="notifications-filter-chips">
                        {categoryOptions.length === 0 && (
                            <span className="notifications-filter-empty">No hay categorías disponibles.</span>
                        )}
                        {categoryOptions.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                className={`notifications-filter-chip ${selectedCategories.includes(option.key) ? 'selected' : ''}`}
                                onClick={() => handleToggleCategory(option.key)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {filteredNotifications.length > 0 ? (
                <div className="notifications-detail-table-wrapper">
                    <table className="notifications-detail-table">
                        <thead>
                            <tr>
                                <th scope="col">Fecha/Hora</th>
                                <th scope="col">Notificación</th>
                                <th scope="col">Tipo</th>
                                <th scope="col">Prioridad</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredNotifications.map((item) => {
                                const dateObj = toDate(item.fecha);
                                const priorityKey = normalizeCategory(item.prioridad || 'normal');
                                return (
                                    <tr key={item.id}>
                                        <td data-label="Fecha/Hora">
                                            {dateObj ? (
                                                <>
                                                    <div>{dateObj.toLocaleDateString('es-MX')}</div>
                                                    <small>{dateObj.toLocaleTimeString('es-MX')}</small>
                                                </>
                                            ) : (
                                                'Sin fecha'
                                            )}
                                        </td>
                                        <td data-label="Notificación">
                                            <div className="notifications-detail-title">{item.titulo || 'Notificación'}</div>
                                            {item.descripcion && (
                                                <small className="notifications-detail-description">{item.descripcion}</small>
                                            )}
                                        </td>
                                        <td data-label="Tipo">
                                            <span className="notifications-tag">{formatLabel(item.categoria)}</span>
                                        </td>
                                        <td data-label="Prioridad">
                                            <span className={`notifications-priority notifications-priority-${priorityKey}`}>
                                                {formatLabel(item.prioridad || 'Normal')}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="notifications-empty">No encontramos notificaciones con los filtros actuales.</p>
            )}
        </div>
    );

    return (
        <div className="notifications-center-overlay" role="dialog" aria-modal="true" aria-labelledby="notificationsCenterTitle" onClick={onClose}>
            <div className="notifications-center-modal" onClick={(event) => event.stopPropagation()}>
                <header className="notifications-center-header">
                    <div>
                        <h2 id="notificationsCenterTitle">Centro de Notificaciones</h2>
                        <p>Consulta la actividad reciente publicada en la plataforma.</p>
                    </div>
                    <button type="button" className="notifications-close" onClick={onClose} aria-label="Cerrar">
                        <FaTimes />
                    </button>
                </header>

                <nav className="notifications-center-tabs" role="tablist">
                    <button
                        id="notificationsSummaryTab"
                        type="button"
                        role="tab"
                        className={tab === 'summary' ? 'active' : ''}
                        aria-selected={tab === 'summary'}
                        onClick={() => setTab('summary')}
                    >
                        Vista Dashboard
                    </button>
                    <button
                        id="notificationsDetailTab"
                        type="button"
                        role="tab"
                        className={tab === 'detail' ? 'active' : ''}
                        aria-selected={tab === 'detail'}
                        onClick={() => setTab('detail')}
                    >
                        Vista Detallada
                    </button>
                </nav>

                <div className="notifications-center-content">
                    {tab === 'summary' ? renderSummaryView() : renderDetailView()}
                </div>
            </div>
        </div>
    );
};

NotificationsCenterModal.propTypes = {
    fraccionamientoId: PropTypes.string,
    categorySet: PropTypes.instanceOf(Set),
    allowedActorRoles: PropTypes.instanceOf(Set),
    onClose: PropTypes.func,
};

NotificationsCenterModal.defaultProps = {
    fraccionamientoId: null,
    categorySet: new Set(),
    allowedActorRoles: undefined,
    onClose: null,
};

export default NotificationsCenterModal;
